// Cast Container Component
// Main container with slide transitions between grid and detail views

import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useNavigation } from "@/contexts/NavigationContext";
import { CastGrid } from "./CastGrid";
import { CastDetailPanel } from "./CastDetailPanel";
import { CastCreateDialog } from "./CastCreateDialog";
import { useLora } from "@/hooks/loras";
import type { Lora, LoraKindSlug } from "@/types/loras";
import "./CastContainer.css";

// Cast is characters only. Props and locations will have their own homes.
export type CastType = "character";

// Cast type colors - using CSS custom properties
export const CAST_TYPE_COLORS: Record<CastType, string> = {
  character: "var(--color-primitive-pink-500)",
};

// RGB channel variants for rgba() opacity expressions
export const CAST_TYPE_COLORS_RGB: Record<CastType, string> = {
  character: "var(--color-primitive-pink-500-rgb)",
};

export interface CastData {
  id: string;
  name: string;
  type: CastType;
  description: string;
  bio: string;
  imageUrl: string | null;
  attributes: Record<string, string>;
}

/**
 * Map a v1 LoRA into the CastData shape the detail panel expects.
 * Cast is characters only (subject LoRAs). Props and locations will have
 * their own homes; until then they remain reachable via direct link.
 */
function loraKindToCastType(_kind: LoraKindSlug): CastType {
  return "character";
}

/**
 * Format a visibility slug for display.
 * e.g. "user_private" → "Private", "team_shared" → "Team Shared"
 */
function formatVisibility(slug: string | undefined | null): string {
  if (!slug) return "—";
  switch (slug) {
    case "user_private":
      return "Private";
    case "team_shared":
      return "Team Shared";
    default:
      // Fallback: convert snake_case to Title Case
      return slug
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
  }
}

function loraToCastData(lora: Lora): CastData {
  return {
    id: lora.id,
    name: lora.name,
    type: loraKindToCastType(lora.kindSlug),
    description: lora.description ?? "",
    bio: lora.description ?? "",
    imageUrl: null,
    attributes: {
      status: lora.statusSlug,
      baseModel: lora.baseModelSlug,
      visibility: formatVisibility(lora.visibilitySlug),
      ...(lora.triggerWord ? { triggerWord: lora.triggerWord } : {}),
    },
  };
}

// Placeholder cast data - hardcoded until backend is implemented
// Exported for use in ContentFilterBar
// Cast is characters only; location/prop placeholders removed.
export const PLACEHOLDER_CASTS: Record<string, CastData> = {
  "1": {
    id: "1",
    name: "Detective Morgan",
    type: "character",
    description: "A world-weary private detective with decades of experience.",
    bio: "Jack Morgan started his career as a beat cop in the rain-soaked streets of the city. After fifteen years on the force, a case gone wrong drove him to hang up his badge and open his own private investigation firm. Now he takes the cases the police won't touch—missing persons, insurance fraud, and the occasional murder that doesn't add up. He's seen too much to be surprised anymore, but still believes in finding the truth, no matter how ugly it turns out to be.",
    imageUrl: null,
    attributes: {
      age: "47",
      sex: "Male",
      height: "6'1\"",
      build: "Lean",
      complexion: "Weathered",
    },
  },
};

interface CastContainerProps {
  projectId: string;
  selectedCastId?: string;
  selectedTab?: string;
}

export function CastContainer({
  projectId,
  selectedCastId,
  selectedTab = "overview",
}: CastContainerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setNavigationMeta } = useNavigation();

  // URL-based dialog state for Create Cast
  const isCreateCastOpen = searchParams.get("modal") === "create-cast";

  // Detail panel animation state
  const [isDetailExiting, setIsDetailExiting] = useState(false);

  // Fetch the selected LoRA from the backend; falls back to placeholder
  // data only for the hardcoded demo IDs (1..4) which won't resolve to a
  // real DB row. Real LoRA ids (uuidv7) hit the backend.
  const loraQuery = useLora(selectedCastId);

  // Find selected cast
  const selectedCast = useMemo(() => {
    if (!selectedCastId) return undefined;
    if (loraQuery.data) {
      return loraToCastData(loraQuery.data);
    }
    // Fallback to placeholder while the LoRA query is loading or for the
    // legacy demo IDs.
    return PLACEHOLDER_CASTS[selectedCastId];
  }, [selectedCastId, loraQuery.data]);

  // Update navigation meta for breadcrumbs when cast is selected
  useEffect(() => {
    if (selectedCast) {
      setNavigationMeta({
        castName: selectedCast.name,
      });
    }
    return () => {
      setNavigationMeta({
        castName: undefined,
      });
    };
  }, [selectedCast, setNavigationMeta]);

  // Handle cast card click - open detail view
  const handleCastClick = useCallback(
    (castId: string) => {
      setSearchParams((prev) => {
        prev.set("cast", castId);
        prev.set("tab", "overview");
        return prev;
      });
    },
    [setSearchParams],
  );

  // Close detail panel (back to grid) with slide-left animation
  const handleCloseDetail = useCallback(() => {
    setIsDetailExiting(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setSearchParams((prev) => {
        prev.delete("cast");
        prev.delete("tab");
        return prev;
      });
      setIsDetailExiting(false);
    }, 350);
  }, [setSearchParams]);

  // Handle tab change in detail panel
  const handleTabChange = useCallback(
    (tab: string) => {
      setSearchParams((prev) => {
        prev.set("tab", tab);
        return prev;
      });
    },
    [setSearchParams],
  );

  // Close create cast dialog
  const handleCloseCreateCast = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("modal");
      return prev;
    });
  }, [setSearchParams]);

  // Is detail panel open?
  const isDetailOpen = !!selectedCastId && !!selectedCast;

  return (
    <div className="cast-container h-full flex flex-col">
      {/* Views Container */}
      <div className="cast-views">
        {/* Grid View (List) */}
        <div
          className={cn(
            "cast-grid-view",
            isDetailOpen && "cast-grid-view--hidden",
          )}
        >
          <div className="p-6">
            <CastGrid projectId={projectId} onCastClick={handleCastClick} />
          </div>
        </div>

        {/* Detail View */}
        <div
          className={cn(
            "cast-detail-view",
            (isDetailOpen || isDetailExiting) && "cast-detail-view--visible",
            isDetailExiting && "cast-detail-view--exiting",
          )}
        >
          {selectedCast && (
            <CastDetailPanel
              key={selectedCastId}
              cast={selectedCast}
              lora={loraQuery.data ?? undefined}
              activeTab={selectedTab}
              onBack={handleCloseDetail}
              onTabChange={handleTabChange}
            />
          )}
        </div>
      </div>

      {/* Create Cast Dialog */}
      <CastCreateDialog
        isOpen={isCreateCastOpen}
        onClose={handleCloseCreateCast}
      />
    </div>
  );
}
